import React from 'react';
import { useTenant } from '../contexts/TenantContext';
import { useCurrentUser } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { useRequirements, useMyRequirements, useVerifyRequirement, usePendingSubmissions, useCreateRequirement } from '../hooks/useRequirements';
import { useDirectory } from '../hooks/useDirectory';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Modal } from '../components/ui/Modal';
import { Input } from '../components/ui/Input';
import { ProgressBar } from '../components/ui/ProgressBar';
import { Spinner } from '../components/ui/Spinner';
import { Avatar } from '../components/ui/Avatar';
import { EvidenceUploadModal } from '../components/requirements/EvidenceUploadModal';
import { 
  CheckCircle2, 
  Clock, 
  AlertCircle, 
  ChevronDown, 
  ChevronRight, 
  FileText, 
  ExternalLink,
  Check,
  X,
  Users,
  ClipboardList
} from 'lucide-react';
import { cn, safeFormat } from '../lib/utils';
import toast from 'react-hot-toast';

// --- Member View Components ---

const RequirementItem: React.FC<{ 
  requirement: any; 
  onUpload: (id: string, name: string) => void 
}> = ({ requirement, onUpload }) => {
  const isDone = requirement.status === 'DONE';
  const isPending = requirement.status === 'PENDING';
  const isRevision = requirement.status === 'REVISION_REQUESTED';

  return (
    <div className="flex items-center justify-between py-3 group">
      <div className="flex items-center gap-3">
        <div className={cn(
          "w-5 h-5 rounded-none flex items-center justify-center border-2 transition-colors",
          isDone ? "bg-teal-500 border-teal-500 text-white" : 
          isPending ? "bg-amber-500 border-amber-500 text-white" :
          isRevision ? "bg-red-500 border-red-500 text-white" :
          "border-black dark:border-white bg-white dark:bg-slate-900"
        )}>
          {isDone && <Check size={12} strokeWidth={3} />}
          {isPending && <Clock size={12} strokeWidth={3} />}
          {isRevision && <AlertCircle size={12} strokeWidth={3} />}
        </div>
        <div>
          <p className={cn(
            "text-sm font-medium transition-colors",
            isDone ? "text-gray-400 line-through" : "text-gray-900 dark:text-white"
          )}>
            {requirement.title}
          </p>
          {requirement.feedback && isRevision && (
            <p className="text-xs text-red-600 mt-0.5">Feedback: {requirement.feedback}</p>
          )}
        </div>
      </div>

      <div className="flex items-center gap-3">
        <Badge variant={
          isDone ? 'teal' : 
          isPending ? 'amber' : 
          isRevision ? 'red' : 'gray'
        }>
          {requirement.status.replace('_', ' ')}
        </Badge>
        
        {!isDone && !isPending && (
          <Button 
            variant="ghost" 
            size="sm" 
            className="opacity-0 group-hover:opacity-100"
            onClick={() => onUpload(requirement.id, requirement.title)}
          >
            Submit Evidence
          </Button>
        )}
      </div>
    </div>
  );
};

const MemberView: React.FC<{ nodeId: string }> = ({ nodeId }) => {
  const { data: groups = [], isLoading } = useMyRequirements(nodeId);
  const { node } = useTenant();
  const [expandedGroups, setExpandedGroups] = React.useState<string[]>([]);
  const [uploadModal, setUploadModal] = React.useState<{ id: string; name: string } | null>(null);

  const toggleGroup = (id: string) => {
    setExpandedGroups(prev => 
      prev.includes(id) ? prev.filter(g => g !== id) : [...prev, id]
    );
  };

  if (isLoading) return <div className="flex justify-center py-20"><Spinner size="lg" /></div>;

  return (
    <div className="space-y-6">
      {groups.map(group => {
        const terms = node?.config?.terminology;
        const isFromParent = group.nodeId !== node?.id;
        
        return (
          <Card key={group.id} className={cn("overflow-hidden p-0", isFromParent && "border-l-4 border-l-gray-400")}>
            <button 
              onClick={() => toggleGroup(group.id)}
              className="w-full flex items-center justify-between p-5 hover:bg-white transition-colors text-left dark:bg-slate-950"
            >
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-3 mb-2">
                  <h3 className="font-semibold text-gray-900 dark:text-white">{group.name}</h3>
                  {isFromParent && group.nodeName && (
                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest bg-white px-1.5 py-0.5 rounded dark:bg-slate-800">
                      {terms?.parentNode || 'From'}: {group.nodeName}
                    </span>
                  )}
                  <span className="text-xs font-mono text-gray-400">
                    {group.progress.completed} of {group.progress.total} complete
                  </span>
                </div>
                <ProgressBar 
                  value={group.progress.percentage} 
                  variant={group.progress.percentage === 100 ? 'teal' : 'blue'} 
                />
              </div>
              <div className="ml-6 text-gray-400">
                {expandedGroups.includes(group.id) ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
              </div>
            </button>

            {expandedGroups.includes(group.id) && (
              <div className="px-5 pb-5 pt-2 divide-y divide-gray-100 animate-in slide-in-from-top-2 duration-200">
                {group.requirements.map(req => (
                  <RequirementItem 
                    key={req.id} 
                    requirement={req} 
                    onUpload={(id, name) => setUploadModal({ id, name })} 
                  />
                ))}
              </div>
            )}
          </Card>
        );
      })}

      {uploadModal && (
        <EvidenceUploadModal 
          isOpen={!!uploadModal}
          onClose={() => setUploadModal(null)}
          requirementId={uploadModal.id}
          requirementName={uploadModal.name}
        />
      )}
    </div>
  );
};

// --- Evaluator View Components ---

const EvaluatorView: React.FC<{ nodeId: string }> = ({ nodeId }) => {
  const { data: groups = [], isLoading: isGroupsLoading } = useRequirements(nodeId);
  const { data: submissions = [], isLoading: isSubmissionsLoading } = usePendingSubmissions(nodeId);
  const { data: membersData } = useDirectory(nodeId);
  const membersList = Array.isArray(membersData) ? membersData : (membersData as any)?.content || [];
  const verifyMutation = useVerifyRequirement(nodeId);
  const { getTerm } = useTenant();
  const [activeTab, setActiveTab] = React.useState<'PENDING' | 'ALL'>('PENDING');

  const handleVerify = (submissionId: string, status: 'DONE' | 'REVISION_REQUESTED') => {
    const feedback = status === 'REVISION_REQUESTED' ? window.prompt(`Enter feedback for the ${getTerm('member')}:`) : undefined;
    if (status === 'REVISION_REQUESTED' && feedback === null) return;

    verifyMutation.mutate({ submissionId, status, feedback: feedback || undefined }, {
      onSuccess: () => toast.success(`Requirement ${status === 'DONE' ? 'approved' : 'revision requested'}`),
      onError: () => toast.error('Failed to verify requirement')
    });
  };

  if (isGroupsLoading || isSubmissionsLoading) return <div className="flex justify-center py-20"><Spinner size="lg" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 bg-white p-1 rounded-none w-fit dark:bg-slate-800">
        <button 
          onClick={() => setActiveTab('PENDING')}
          className={cn(
            "px-4 py-1.5 rounded-none text-xs font-semibold transition-all flex items-center gap-2",
            activeTab === 'PENDING' ? "bg-white text-blue-700 shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] dark:bg-slate-900" : "text-gray-500 hover:text-gray-700"
          )}
        >
          <Clock size={14} />
          Pending Review ({submissions.length})
        </button>
        <button 
          onClick={() => setActiveTab('ALL')}
          className={cn(
            "px-4 py-1.5 rounded-none text-xs font-semibold transition-all flex items-center gap-2",
            activeTab === 'ALL' ? "bg-white text-blue-700 shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] dark:bg-slate-900" : "text-gray-500 hover:text-gray-700"
          )}
        >
          <Users size={14} />
          All {getTerm('member')}s
        </button>
      </div>

      {activeTab === 'PENDING' ? (
        <div className="space-y-4">
          {submissions.length > 0 ? (
            submissions.map(sub => (
              <Card key={sub.id} className="group">
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-6">
                  <div className="flex gap-4">
                    <Avatar userId={sub.memberId} initials={sub.memberName[0]} size="md" />
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-semibold text-gray-900 dark:text-white">{sub.memberName}</span>
                        <span className="text-gray-300">•</span>
                        <span className="text-xs text-gray-500">{safeFormat(sub.submittedAt, 'MMM d, h:mm a')}</span>
                      </div>
                      <p className="text-sm font-medium text-blue-700 mb-3">{sub.requirementName}</p>
                      
                      <div className="bg-white rounded-none p-3 border-2 border-black dark:border-white mb-4 dark:bg-slate-950">
                        <p className="text-xs text-gray-700 leading-relaxed italic">"{sub.notes || 'No notes provided.'}"</p>
                      </div>

                      <a 
                        href={sub.evidenceUrl} 
                        target="_blank" 
                        rel="noreferrer"
                        className="inline-flex items-center gap-2 text-xs font-semibold text-navy-900 dark:text-white hover:underline"
                      >
                        <FileText size={14} />
                        View Evidence File
                        <ExternalLink size={12} />
                      </a>
                    </div>
                  </div>

                  <div className="flex md:flex-col gap-2">
                    <Button 
                      variant="primary" 
                      size="sm" 
                      className="bg-teal-700 hover:bg-teal-800"
                      onClick={() => handleVerify(sub.id, 'DONE')}
                      isLoading={verifyMutation.isPending}
                    >
                      <Check size={16} />
                      Approve
                    </Button>
                    <Button 
                      variant="secondary" 
                      size="sm"
                      onClick={() => handleVerify(sub.id, 'REVISION_REQUESTED')}
                      isLoading={verifyMutation.isPending}
                    >
                      <AlertCircle size={16} />
                      Request Revision
                    </Button>
                  </div>
                </div>
              </Card>
            ))
          ) : (
            <div className="text-center py-20 bg-white border-2 border-black dark:border-white border-dashed border-black dark:border-white rounded-none dark:bg-slate-900">
              <CheckCircle2 size={40} className="mx-auto text-gray-300 mb-4" />
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Queue Clear!</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">No pending submissions to review.</p>
            </div>
          )}
        </div>
      ) : (
        <Card className="overflow-hidden p-0">
          <div className="p-5 border-b border-black dark:border-white">
            <h3 className="font-semibold text-gray-900 dark:text-white">Compliance Grid</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Overview of all member progress across requirements.</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-white border-b border-black dark:border-white dark:bg-slate-950">
                  <th className="p-4 text-[10px] font-bold text-gray-400 uppercase tracking-widest font-mono">Member</th>
                  {groups?.[0]?.requirements.map(req => (
                    <th key={req.id} className="p-4 text-[10px] font-bold text-gray-400 uppercase tracking-widest font-mono text-center min-w-[100px]">
                      {req.title}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {membersList.map((member: any) => {
                  const fullName = member.name || `${member.firstName || ''} ${member.lastName || ''}`.trim() || member.email || 'Member';
                  const initials = fullName.split(' ').map((p: string) => p[0]).join('').slice(0, 2).toUpperCase() || 'M';
                  return (
                    <tr key={member.id || member.userId} className="hover:bg-white transition-colors dark:bg-slate-950">
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <Avatar userId={member.userId || member.id} initials={initials} size="sm" />
                          <span className="text-xs font-medium text-gray-900 dark:text-white">{fullName}</span>
                        </div>
                      </td>
                      {groups?.[0]?.requirements.map(req => {
                        const sub = submissions.find((s: any) => s.memberId === (member.userId || member.id) && (s.requirementId === req.id || s.requirementName === req.title));
                        const dotColor = sub?.status === 'DONE' ? 'bg-teal-500' : sub ? 'bg-amber-500' : 'bg-gray-200';
                        return (
                          <td key={req.id} className="p-4 text-center">
                            <div className={cn("w-2 h-2 rounded-none mx-auto", dotColor)} />
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
                {membersList.length === 0 && (
                  <tr>
                    <td colSpan={(groups?.[0]?.requirements?.length || 0) + 1} className="p-6 text-center text-xs text-gray-400">
                      No members found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
};

// --- Main Page ---

export const RequirementsPage = () => {
  const { node } = useTenant();
  const { can, isAdmin: isGlobalAdmin } = usePermissions();
  const createRequirement = useCreateRequirement(node?.id || '');
  const [isCreateOpen, setIsCreateOpen] = React.useState(false);
  const [newReqTitle, setNewReqTitle] = React.useState('');
  const [newReqDescription, setNewReqDescription] = React.useState('');
  
  const isEvaluator = isGlobalAdmin || can('EVIDENCE_VERIFY') || can('REQUIREMENT_MANAGE') || can('verify_requirement');

  const handleCreateRequirement = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newReqTitle.trim()) return;
    createRequirement.mutate(
      { title: newReqTitle.trim(), description: newReqDescription.trim(), required: true },
      {
        onSuccess: () => {
          toast.success('Requirement created');
          setIsCreateOpen(false);
          setNewReqTitle('');
          setNewReqDescription('');
        },
        onError: () => toast.error('Failed to create requirement')
      }
    );
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-lg md:text-2xl font-semibold text-gray-900 dark:text-white">
            {isEvaluator ? 'Evaluator Queue' : 'Achievements & Requirements'}
          </h1>
          <p className="text-xs md:text-sm text-gray-500">
            {isEvaluator 
              ? 'Review and verify member evidence submissions.' 
              : 'Track your progress and submit evidence for achievements.'}
          </p>
        </div>
        
        {isEvaluator ? (
          <Button variant="primary" size="sm" onClick={() => setIsCreateOpen(true)}>
            + New Requirement
          </Button>
        ) : (
          <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-none text-xs font-semibold">
            <ClipboardList size={14} />
            Checklist View
          </div>
        )}
      </div>

      {isEvaluator ? (
        <EvaluatorView nodeId={node?.id || ''} />
      ) : (
        <MemberView nodeId={node?.id || ''} />
      )}

      <Modal isOpen={isCreateOpen} onClose={() => setIsCreateOpen(false)} title="Create Requirement">
        <form onSubmit={handleCreateRequirement} className="space-y-4">
          <Input
            label="Requirement Title"
            placeholder="e.g. Annual Waiver Submission"
            value={newReqTitle}
            onChange={(e) => setNewReqTitle(e.target.value)}
            required
          />
          <Input
            label="Description"
            placeholder="Instructions for members submitting evidence..."
            value={newReqDescription}
            onChange={(e) => setNewReqDescription(e.target.value)}
          />
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="secondary" onClick={() => setIsCreateOpen(false)}>Cancel</Button>
            <Button type="submit" variant="primary" isLoading={createRequirement.isPending}>Create</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};
